import {
  blockMessages,
  stmtExprs,
  walkExpr,
  walkStmts,
  type BlockMessages,
  type Expr,
  type Program,
  type Stmt,
} from '@pebblecode/codegen';
import type { UiLanguage } from '@pebblecode/shared';

export type PreviewCategory = 'logic' | 'loops' | 'math' | 'variables' | 'text' | 'io' | 'lists';

export interface PreviewLine {
  text: string;
  category: PreviewCategory;
  depth: number;
}

/** What a list holds, in the student's language — the same words the block dropdown shows. */
const ELEM_WORD = (m: BlockMessages, elem: 'int' | 'double' | 'string' | 'bool'): string =>
  elem === 'int'
    ? m.PC_ELEM_INT
    : elem === 'double'
      ? m.PC_ELEM_DOUBLE
      : elem === 'string'
        ? m.PC_ELEM_TEXT
        : m.PC_ELEM_BOOL;

const WORDS: Record<UiLanguage, { if: string; and: string; or: string; length: string }> = {
  en: { if: 'if', and: 'and', or: 'or', length: 'length of' },
  ro: { if: 'dacă', and: 'și', or: 'sau', length: 'lungimea lui' },
};

const ARITH: Record<string, string> = { '+': '+', '-': '−', '*': '×', '/': '÷' };
const CMP: Record<string, string> = {
  '==': '=',
  '!=': '≠',
  '<': '<',
  '<=': '≤',
  '>': '>',
  '>=': '≥',
};

function fill(template: string, ...args: string[]): string {
  return template.replace(/%(\d)/g, (_, i: string) => args[Number(i) - 1] ?? '');
}

/**
 * A program as block labels in the UI language ("set s to s + n mod 10"), for the home page's
 * "pick up where you left off" miniature.
 */
export function previewLines(
  program: Program,
  lang: UiLanguage,
  max = 5,
  only?: string,
): PreviewLine[] {
  const m = blockMessages[lang];
  const w = WORDS[lang];

  const expr = (e: Expr): string => {
    switch (e.k) {
      case 'num':
        return String(e.value);
      case 'text':
        return `“${e.value}”`;
      case 'bool':
        return String(e.value);
      case 'var':
        return e.name;
      case 'read':
        return fill(
          m.PC_READ,
          e.as === 'int' ? m.PC_READ_INT : e.as === 'double' ? m.PC_READ_DOUBLE : m.PC_READ_TEXT,
        );
      case 'arith':
        if (e.op === 'idiv') return fill(m.PC_INT_DIV, expr(e.left), expr(e.right));
        if (e.op === 'mod') return fill(m.PC_MOD, expr(e.left), expr(e.right));
        return `${expr(e.left)} ${ARITH[e.op] ?? e.op} ${expr(e.right)}`;
      case 'compare':
        return `${expr(e.left)} ${CMP[e.op] ?? e.op} ${expr(e.right)}`;
      case 'logic':
        return `${expr(e.left)} ${e.op === 'and' ? w.and : w.or} ${expr(e.right)}`;
      case 'not':
        return fill(m.LOGIC_NEGATE_TITLE, expr(e.arg));
      case 'join':
        return fill(m.PC_JOIN, expr(e.left), expr(e.right));
      case 'length':
        return `${w.length} ${expr(e.arg)}`;
      case 'at':
        return `${e.name}[${expr(e.index)}]${e.index2 ? `[${expr(e.index2)}]` : ''}`;
      case 'code':
        return fill(m.PC_CODE, expr(e.arg));
      case 'chr':
        return fill(m.PC_FROM_CODE, expr(e.arg));
      case 'case':
        return fill(m.PC_CASE, e.upper ? m.PC_CASE_UPPER : m.PC_CASE_LOWER, expr(e.arg));
      case 'find':
        return fill(m.PC_FIND, expr(e.needle), expr(e.hay));
      case 'substr':
        return fill(m.PC_SUBSTR, expr(e.arg), expr(e.from), expr(e.len));
      case 'size':
        return fill(m.PC_LIST_SIZE, e.dim === 2 ? m.PC_SIZE_COLUMNS : m.PC_SIZE_PLACES, e.name);
      case 'missing':
        return '…';
    }
  };

  const line = (s: Stmt): { text: string; category: PreviewCategory } => {
    switch (s.k) {
      case 'set':
        return { text: fill(m.VARIABLES_SET, s.name, expr(s.value)), category: 'variables' };
      case 'newArray': {
        const [rows, cols] = s.dims;
        const elem = ELEM_WORD(m, s.elem);
        return {
          text: cols
            ? fill(m.PC_TABLE_NEW, s.name, expr(rows), expr(cols), elem, String(s.base))
            : fill(m.PC_LIST_NEW, s.name, expr(rows), elem, String(s.base)),
          category: 'lists',
        };
      }
      case 'setAt':
        return {
          text: s.index2
            ? fill(m.PC_TABLE_SET, s.name, expr(s.index), expr(s.index2), expr(s.value))
            : fill(m.PC_LIST_SET, s.name, expr(s.index), expr(s.value)),
          category: 'lists',
        };
      case 'change':
        return { text: fill(m.MATH_CHANGE_TITLE, s.name, expr(s.delta)), category: 'variables' };
      case 'print':
        return s.end === 'newline'
          ? { text: fill(m.PC_PRINT, expr(s.value)), category: 'io' }
          : {
              text: fill(
                m.PC_PRINT_INLINE,
                expr(s.value),
                s.end === 'space' ? m.PC_END_SPACE : m.PC_END_NONE,
              ),
              category: 'io',
            };
      case 'if':
        return { text: `${w.if} ${expr(s.branches[0]!.cond)}`, category: 'logic' };
      case 'repeat':
        return { text: fill(m.PC_REPEAT, expr(s.times)), category: 'loops' };
      case 'while':
        return { text: fill(m.PC_WHILE, expr(s.cond)), category: 'loops' };
      case 'for':
        return {
          text: fill(
            m.PC_FOR,
            s.name,
            expr(s.from),
            s.down ? m.PC_FOR_DOWN : m.PC_FOR_UP,
            expr(s.to),
          ),
          category: 'loops',
        };
      case 'break':
        return { text: m.PC_BREAK, category: 'loops' };
    }
  };

  if (only) {
    for (const s of walkStmts(program.body)) {
      if (s.id === only) return [{ ...line(s), depth: 0 }];
      for (const e of stmtExprs(s)) {
        for (const x of walkExpr(e)) {
          if (x.id === only && x.k !== 'missing')
            return [{ text: expr(x), category: 'math', depth: 0 }];
        }
      }
    }
    return [];
  }

  const out: PreviewLine[] = [{ text: m.PC_START, category: 'io', depth: 0 }];
  const visit = (body: readonly Stmt[], depth: number) => {
    for (const s of body) {
      if (out.length >= max) return;
      out.push({ ...line(s), depth });
      if (s.k === 'if') visit(s.branches[0]!.body, depth + 1);
      if (s.k === 'repeat' || s.k === 'while' || s.k === 'for') visit(s.body, depth + 1);
    }
  };
  visit(program.body, 0);
  return out;
}

/** One block's label ("n mod 10"), for the tutor's "Explain this block" bubble. */
export function describeBlock(program: Program, blockId: string, lang: UiLanguage): string | null {
  return previewLines(program, lang, 1, blockId)[0]?.text ?? null;
}
